'use client';

import { KidsLoading, isSmallDevice } from '../../../../utils';
import React, { useMemo, type ComponentProps } from 'react';
import { CouponDetail } from '../coupon-detail/coupon-detail';
import { couponListStyles } from './coupon-list.styles';
import { useTranslation } from 'next-i18next';
import type { SalesRule } from '../../../../types/front.graphql';
import { View } from '../../../../utils/react-native-stubs';
import { observer, use$ } from '@legendapp/state/react';
import { useCouponContext } from '../../contexts/coupon.context';
import type { CouponListInnerProps, CouponListProps } from './coupon-list.types';
import { withAppBottomSheet, CouponSection as UiCouponList, Text } from '@kidslabs/mario-nextjs-react-ui-core';

const CouponListInner = observer((props: CouponListInnerProps) => {
    const { t } = useTranslation('flash-sale');
    const styles = couponListStyles();
    const { bottomSheet, coupons, onSelectCoupon, allDisabled } = props;
    const couponContext = useCouponContext();

    const loadingState = couponContext?.state?.loading;

    const selectedCoupon = use$(couponContext?.couponState$?.selectedCoupon);
    const selectedCouponForView: ComponentProps<typeof UiCouponList>['selectedCoupon'] = useMemo(() => {
        return {
            ...selectedCoupon,
            code: selectedCoupon?.code ?? '',
            description: selectedCoupon?.description ?? '',
            from_date: selectedCoupon?.from_date ?? '',
            is_active: selectedCoupon?.is_active ?? false,
            is_valid: selectedCoupon?.is_valid ?? false,
            name: selectedCoupon?.name ?? '',
            to_date: selectedCoupon?.to_date ?? '',
        };
    }, [selectedCoupon]);

    // When allDisabled, override all sections to disabled
    const effectiveCoupons = useMemo(() => {
        if (!allDisabled) return coupons;
        return coupons.map(section => ({
            ...section,
            isDisabled: true,
        }));
    }, [coupons, allDisabled]);

    // When allDisabled, use no-op handler for selection
    const effectiveOnSelectCoupon = useMemo(
        () => (allDisabled ? () => {} : onSelectCoupon),
        [allDisabled, onSelectCoupon]
    );

    const onCouponDetail = (coupon: SalesRule) => {
        bottomSheet.inject(
            <CouponDetail isAvailableCoupon coupon={coupon} bottomSheetProps={props} dataType={'salesRule'} />,
            {
                title: t('text_detail_coupon'),
                titleProps: {
                    variant: 'h3',
                    style: {
                        color: 'var(--color-primary-100)',
                        fontWeight: 'var(--font-weight-bold)',
                        textAlign: 'center',
                        width: '100%',
                    },
                },
                containerStyle: styles.headerBottomSheet,
                closeIconProps: {
                    icon: 'blank',
                    disabled: true,
                    iconColor: 'transparent',
                },
            },
            {
                enableDynamicSizing: false,
                scrollEnabled: false,
                height: 'full',
            },
            undefined,
            {
                pressBehavior: 'close',
            }
        );
        bottomSheet.show();
    };

    const HeaderComponent: React.FC = () => <Text size="large" weight="bold" style={styles.txtHeader}></Text>;

    const isLoading = loadingState?.add || loadingState?.remove;

    return (
        <>
            {isLoading && <KidsLoading containerStyle={styles.loadingMask} />}
            <View style={styles.container}>
                <UiCouponList
                    coupons={effectiveCoupons}
                    selectedCoupon={selectedCouponForView}
                    onSelectCoupon={effectiveOnSelectCoupon as (coupon: unknown) => void}
                    onCouponDetail={onCouponDetail as (coupon: unknown) => void}
                    CouponListHeaderComponent={HeaderComponent}
                    emptyText={t('text_no_suitable_coupons')}
                />
            </View>
        </>
    );
});

const CouponListContent = withAppBottomSheet(CouponListInner);

export const CouponList: React.FC<CouponListProps> = ({ coupons, onSelectCoupon, allDisabled }) => {
    const snapPoints = useMemo(() => (isSmallDevice() ? ['90%', '90%'] : ['75%', '75%']), []);

    return (
        <CouponListContent
            snapPoints={snapPoints}
            coupons={coupons}
            onSelectCoupon={onSelectCoupon}
            allDisabled={allDisabled}
        />
    );
};
